import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RefreshCcw, ArrowDownUp } from "lucide-react";
import { api } from "@/lib/api";

function StatusBadge({ status }) {
  if (status === "success") {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">success</Badge>;
  }
  return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">failure</Badge>;
}

function ResponseDialog({ attempt }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`view-response-${attempt.id}`}>View</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Attempt {attempt.id.slice(0, 8)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1">Response summary</div>
            <div className="text-sm text-slate-800 break-words">{attempt.response_summary || "—"}</div>
          </div>
          {attempt.http_status !== null && attempt.http_status !== undefined && (
            <div className="text-xs text-slate-500">HTTP status: <span className="font-mono">{attempt.http_status}</span></div>
          )}
          {attempt.request_payload_summary && (
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1">Request payload</div>
              <pre className="text-xs bg-zinc-50 border border-zinc-200 rounded p-3 overflow-auto font-mono">
{JSON.stringify(attempt.request_payload_summary, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Logs() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortDesc, setSortDesc] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attempts");
      setAttempts(data.attempts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sorted = [...attempts].sort((a, b) => {
    const da = new Date(a.created_at).getTime();
    const db = new Date(b.created_at).getTime();
    return sortDesc ? db - da : da - db;
  });

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="logs-page">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Audit</div>
          <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-slate-900">Attempt Logs</h1>
          <p className="mt-1.5 text-sm sm:text-base text-slate-600">Every draft submission and its outcome, latest first.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-logs" className="shrink-0">
          <RefreshCcw className="w-4 h-4" /><span className="hidden sm:inline">Refresh</span>
        </Button>
      </header>

      {/* Mobile: card list */}
      <div className="lg:hidden space-y-3">
        <button
          onClick={() => setSortDesc((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
          data-testid="sort-timestamp-mobile"
        >
          {sortDesc ? "Newest first" : "Oldest first"} <ArrowDownUp className="w-3 h-3" />
        </button>

        {loading ? (
          <Card className="p-6 text-center text-slate-500 text-sm">Loading…</Card>
        ) : sorted.length === 0 ? (
          <Card className="p-6 text-center text-slate-500 text-sm">No attempts yet.</Card>
        ) : (
          sorted.map((a) => (
            <Card key={a.id} className="p-4 border-zinc-200" data-testid={`log-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate" title={a.file_name || ""}>
                    {a.file_name || "—"}
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums mt-0.5">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </div>
                <StatusBadge status={a.status} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-slate-500">Account</div>
                  <div className="text-slate-800 truncate">{a.account_label || a.target_account || "—"}</div>
                </div>
                <div>
                  <div className="text-slate-500">HTTP</div>
                  <div className="text-slate-800 font-mono">{a.http_status ?? "—"}</div>
                </div>
                {a.caption && (
                  <div className="col-span-2">
                    <div className="text-slate-500">Caption</div>
                    <div className="text-slate-800 truncate" title={a.caption}>{a.caption}</div>
                  </div>
                )}
                <div className="col-span-2">
                  <div className="text-slate-500">Response</div>
                  <div className="text-slate-800 line-clamp-2 break-words">{a.response_summary || "—"}</div>
                </div>
              </div>
              <div className="mt-3">
                <ResponseDialog attempt={a} />
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <Card className="hidden lg:block border-zinc-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => setSortDesc((v) => !v)} data-testid="sort-timestamp">
                  Timestamp <ArrowDownUp className="w-3 h-3" />
                </button>
              </TableHead>
              <TableHead>File</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Drive File ID</TableHead>
              <TableHead>Folder ID</TableHead>
              <TableHead>Caption</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Response</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
            ) : sorted.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No attempts yet.</TableCell></TableRow>
            ) : (
              sorted.map((a) => (
                <TableRow key={a.id} data-testid={`log-row-${a.id}`}>
                  <TableCell className="tabular-nums whitespace-nowrap text-slate-700">
                    {new Date(a.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate" title={a.file_name || ""}>{a.file_name || "—"}</TableCell>
                  <TableCell className="text-slate-700 max-w-[140px] truncate" title={a.account_label || ""}>{a.account_label || a.target_account || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600 max-w-[140px] truncate" title={a.drive_file_id}>{a.drive_file_id}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600 max-w-[120px] truncate" title={a.folder_id || ""}>{a.folder_id || "—"}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-slate-700" title={a.caption || ""}>{a.caption || "—"}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell><ResponseDialog attempt={a} /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
